// =============================================================================
// features/documents/components/DocumentsPanel.tsx — Componente único de
// documentos, renderizado nas 3 superfícies (CRM/análise/contrato) — F46-S08.
//
// scope='lead'                → central do CRM: tudo do lead, agrupado por
//                                categoria do tipo de documento.
// scope='analysis'|'contract' → filtra pelo contexto; upload já nasce
//                                vinculado (analysisId/contractId).
//
// Estados: carregando (skeleton), vazio (com CTA), erro (com retry), sucesso.
// A flag `documents.enabled` e as permissões RBAC são o gate real — este
// componente nunca assume, sempre consulta `useDocumentsGate()`.
// =============================================================================

import type { Document, DocumentType } from '@elemento/shared-schemas';
import * as React from 'react';

import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../components/ui/Toast';
import {
  useContextDocuments,
  useDeleteDocument,
  useDocumentsGate,
  useDocumentTypesCatalog,
  useDownloadDocument,
  useLeadDocuments,
} from '../hooks';

import { DocumentCard, humanizeDocumentTypeKey, resolveDocumentDisplayName } from './DocumentCard';
import { DocumentPreviewDrawer } from './DocumentPreviewDrawer';
import { DocumentUploadDialog } from './DocumentUploadDialog';

// ─── Helpers puros (testados em __tests__/DocumentsPanel.test.ts) ──────────

export interface DocumentActionsResolved {
  canDownload: boolean;
  canDelete: boolean;
  canWrite: boolean;
}

/** `readOnly` esconde escrita/exclusão na UI — nunca substitui o gate real da
 * API (nota do slot). Download não é mutação: não é afetado por `readOnly`. */
export function resolveDocumentActions(
  gate: { canDownload: boolean; canDelete: boolean; canWrite: boolean },
  readOnly: boolean,
): DocumentActionsResolved {
  return {
    canDownload: gate.canDownload,
    canDelete: gate.canDelete && !readOnly,
    canWrite: gate.canWrite && !readOnly,
  };
}

export interface DocumentGroup {
  key: string;
  label: string;
  documents: Document[];
}

/**
 * Teto de altura do painel de documentos (F49-S01) — bloco principal
 * (`60vh`), mesmo padrão da Timeline do CRM. A contagem "Documentos (N)" já
 * fica no `<h2>` estático, fora desta área rolável.
 */
export const DOCUMENTS_SCROLL_CLASS = 'max-h-[60vh] overflow-y-auto pr-1';

const CATEGORY_LABEL: Record<string, string> = {
  identificacao: 'Identificação',
  renda: 'Renda',
  endereco: 'Endereço',
  negocio: 'Negócio',
  garantia: 'Garantia',
  contrato: 'Contrato',
  juridico: 'Jurídico',
  outros: 'Outros',
};

/** Agrupa por categoria do catálogo. Sem catálogo disponível (catálogo vazio
 * ou erro 403 defensivo — ver api.ts), devolve um grupo único em vez de
 * quebrar a listagem. */
export function groupDocumentsByCategory(
  documents: Document[],
  catalog: DocumentType[],
): DocumentGroup[] {
  if (documents.length === 0) return [];
  if (catalog.length === 0) return [{ key: 'all', label: 'Documentos', documents }];

  const catalogByKey = new Map(catalog.map((t) => [t.key, t]));
  const buckets = new Map<string, Document[]>();
  for (const doc of documents) {
    const category = catalogByKey.get(doc.document_type_key)?.category ?? 'outros';
    const bucket = buckets.get(category) ?? [];
    bucket.push(doc);
    buckets.set(category, bucket);
  }
  return Array.from(buckets.entries()).map(([key, docs]) => ({
    key,
    label: CATEGORY_LABEL[key] ?? key,
    documents: docs,
  }));
}

export function resolveTypeLabel(doc: Document, catalog: DocumentType[]): string {
  return (
    catalog.find((t) => t.key === doc.document_type_key)?.label ??
    humanizeDocumentTypeKey(doc.document_type_key)
  );
}

// ─── Estados visuais compartilhados ─────────────────────────────────────────

function PanelSkeleton(): React.JSX.Element {
  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
    >
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={String(i)}
          className="rounded-md p-4 flex flex-col gap-3 animate-pulse h-[180px]"
          style={{
            background: 'var(--bg-elev-1)',
            border: '1px solid var(--border)',
            boxShadow: 'var(--elev-2)',
          }}
        />
      ))}
    </div>
  );
}

function PanelEmpty({
  canWrite,
  onUpload,
}: {
  canWrite: boolean;
  onUpload: () => void;
}): React.JSX.Element {
  return (
    <div
      className="rounded-md p-10 flex flex-col items-center gap-3 text-center"
      style={{
        background: 'var(--bg-elev-1)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--elev-1)',
      }}
    >
      <p
        className="font-display font-bold"
        style={{ fontSize: 'var(--text-lg)', color: 'var(--text)' }}
      >
        Nenhum documento ainda
      </p>
      <p className="font-sans" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-2)' }}>
        Documentos anexados aqui ficam disponíveis para toda a equipe.
      </p>
      {canWrite && (
        <Button variant="primary" size="sm" onClick={onUpload}>
          Anexar documento
        </Button>
      )}
    </div>
  );
}

function PanelError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  return (
    <div
      className="rounded-md p-8 flex flex-col items-center gap-3 text-center"
      style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)' }}
    >
      <p
        className="font-sans font-semibold"
        style={{ fontSize: 'var(--text-sm)', color: 'var(--danger)' }}
      >
        Não foi possível carregar os documentos.
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Tentar novamente
      </Button>
    </div>
  );
}

// ─── Componente principal ───────────────────────────────────────────────────

export interface DocumentsPanelProps {
  leadId: string;
  scope: 'lead' | 'analysis' | 'contract';
  /** analysisId | contractId — obrigatório quando scope !== 'lead'. */
  contextId?: string | undefined;
  readOnly?: boolean | undefined;
}

export function DocumentsPanel({
  leadId,
  scope,
  contextId,
  readOnly = false,
}: DocumentsPanelProps): React.JSX.Element | null {
  const gate = useDocumentsGate();
  const { toast } = useToast();

  // As duas queries coexistem (nunca useQuery+useInfiniteQuery na mesma key —
  // aqui nem é o caso, mas seguimos o padrão de nunca alternar hooks
  // condicionalmente: cada uma se auto-desliga via `enabled`).
  const leadQuery = useLeadDocuments(leadId, { enabled: gate.canRead && scope === 'lead' });
  const contextQuery = useContextDocuments({
    leadId,
    scope: scope === 'contract' ? 'contract' : 'analysis',
    contextId: contextId ?? '',
    enabled: gate.canRead && scope !== 'lead',
  });

  const { data: catalog } = useDocumentTypesCatalog();
  const download = useDownloadDocument();
  const del = useDeleteDocument();

  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [previewDoc, setPreviewDoc] = React.useState<Document | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  if (gate.isLoading) return <PanelSkeleton />;
  if (!gate.canRead) return null;

  const isLoading = scope === 'lead' ? leadQuery.isLoading : contextQuery.isLoading;
  const isError = scope === 'lead' ? leadQuery.isError : contextQuery.isError;
  const refetch = scope === 'lead' ? leadQuery.refetch : contextQuery.refetch;
  const documents: Document[] =
    scope === 'lead' ? (leadQuery.data ?? []) : (contextQuery.data?.data ?? []);

  const actions = resolveDocumentActions(gate, readOnly);
  const groups = scope === 'lead' ? groupDocumentsByCategory(documents, catalog ?? []) : null;

  const handleDownload = (doc: Document): void => {
    // F46-S31: o download usa o nome que o agente organizou
    // (`display_name`) quando houver — é o nome que ele escolheu para este
    // arquivo. Sem nenhum dos dois, cai no aviso de nome genérico.
    const displayName = resolveDocumentDisplayName(doc);
    if (!displayName) {
      toast('Documento sem nome de arquivo salvo — o download usará um nome genérico.', 'info');
    }
    download.download({
      documentId: doc.id,
      filename: displayName ?? `documento-${doc.id}`,
    });
  };

  const handleDelete = (doc: Document): void => {
    if (!window.confirm('Remover este documento? Ele deixará de aparecer para a equipe.')) return;
    setDeletingId(doc.id);
    del.mutate(doc.id, { onSettled: () => setDeletingId(null) });
  };

  const renderCard = (doc: Document): React.JSX.Element => (
    <DocumentCard
      key={doc.id}
      document={doc}
      typeLabel={resolveTypeLabel(doc, catalog ?? [])}
      canDownload={actions.canDownload}
      canDelete={actions.canDelete}
      onDownload={() => handleDownload(doc)}
      onDelete={() => handleDelete(doc)}
      onPreview={() => setPreviewDoc(doc)}
      isDownloading={download.isDownloading}
      isDeleting={deletingId === doc.id}
    />
  );

  return (
    <section aria-label="Documentos" className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2
          className="font-display font-bold"
          style={{ fontSize: 'var(--text-xl)', color: 'var(--text)' }}
        >
          Documentos {documents.length > 0 && `(${documents.length})`}
        </h2>
        {actions.canWrite && documents.length > 0 && (
          <Button variant="primary" size="sm" onClick={() => setUploadOpen(true)}>
            Anexar documento
          </Button>
        )}
      </div>

      {isLoading && <PanelSkeleton />}
      {!isLoading && isError && <PanelError onRetry={() => void refetch()} />}
      {!isLoading && !isError && documents.length === 0 && (
        <PanelEmpty canWrite={actions.canWrite} onUpload={() => setUploadOpen(true)} />
      )}

      {!isLoading && !isError && documents.length > 0 && (
        <div className={DOCUMENTS_SCROLL_CLASS}>
          {groups ? (
            <div className="flex flex-col gap-6">
              {groups.map((group) => (
                <div key={group.key} className="flex flex-col gap-3">
                  <h3
                    className="font-sans font-semibold uppercase"
                    style={{
                      fontSize: 'var(--text-xs)',
                      letterSpacing: '0.08em',
                      color: 'var(--text-3)',
                    }}
                  >
                    {group.label}
                  </h3>
                  <div
                    className="grid gap-4"
                    style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
                  >
                    {group.documents.map(renderCard)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
            >
              {documents.map(renderCard)}
            </div>
          )}
        </div>
      )}

      {actions.canWrite && (
        <DocumentUploadDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          leadId={leadId}
          scope={scope}
          contextId={contextId}
        />
      )}

      <DocumentPreviewDrawer
        open={previewDoc !== null}
        onClose={() => setPreviewDoc(null)}
        document={previewDoc}
        canDownload={actions.canDownload}
      />
    </section>
  );
}
